/**
 * The aim icon of a spell with an area («Примерить зону от своего токена»): in the battle HUD and in the «Бой» tab.
 * The press hands the spell's area to the canvas (spell-aoe-fit-store), which fits it from the caster token.
 */
import { useMemo } from 'react';
import { Crosshair } from 'lucide-react';
import { toast } from 'sonner';
import { canFitSpellAoe, parseSpellRangeFt } from '@roll1/shared/spell-aoe';
import type { HudSpell } from '../../lib/character-hud-store';
import { useOptionalTokenContext } from '../../contexts/TokenContext';
import { useOptionalSelectionContext } from '../../contexts/SelectionContext';
import { useOptionalSessionContext } from '../../contexts/SessionContext';
import { cancelSpellAoeFit, requestSpellAoeFit, useSpellAoeFitRequest } from '../../lib/spell-aoe-fit-store';
import { useI18n } from '../../i18n';
import { Button } from '@/components/ui/button';

/** Does the spell carry an area the aim icon can fit? */
export function hasFittableAoe(spell: HudSpell): spell is HudSpell & { aoe: NonNullable<HudSpell['aoe']> } {
  return !!spell.aoe && canFitSpellAoe(spell.aoe);
}

export interface SpellAoeAim {
  /** The caster token on the map on screen, or null (no token → the icon is inactive). */
  tokenId: string | null;
  /** Why the icon is inactive (the tooltip), or null. */
  disabledReason: string | null;
  /** Name of the spell being fitted now, or null. */
  fittingSpell: string | null;
  start: (spell: HudSpell) => void;
  cancel: () => void;
}

/**
 * The player fits from the token of the character sheet on the current map; the GM — from the selected token
 * (the token of the sheet when nothing is selected).
 */
export function useSpellAoeAim(sheetId: string | undefined): SpellAoeAim {
  const { t } = useI18n();
  const tokenCtx = useOptionalTokenContext();
  const selection = useOptionalSelectionContext();
  const session = useOptionalSessionContext();
  const request = useSpellAoeFitRequest();
  const tokens = tokenCtx?.tokens.tokens;
  const isGM = session?.isGM ?? false;
  const selectedIds = selection?.selectedTokenIds;

  const tokenId = useMemo(() => {
    if (!tokens) return null;
    if (isGM && selectedIds) {
      const picked = tokens.find(tk => selectedIds.has(tk.id));
      if (picked) return picked.id;
    }
    if (!sheetId) return null;
    return tokens.find(tk => tk.characterSheetId === sheetId)?.id ?? null;
  }, [tokens, isGM, selectedIds, sheetId]);

  const disabledReason = tokenId ? null : t(isGM ? 'spellAoe.noTokenGm' : 'spellAoe.noToken');

  return {
    tokenId,
    disabledReason,
    fittingSpell: request?.spellName ?? null,
    start: (spell) => {
      if (!tokenId) { toast(t('spellAoe.noToken')); return; }
      if (!hasFittableAoe(spell)) return;
      if (request && request.spellName === spell.name && request.tokenId === tokenId) { cancelSpellAoeFit(); return; }
      requestSpellAoeFit({
        spellName: spell.name,
        aoe: spell.aoe,
        rangeFt: spell.aoe.rangeFt ?? parseSpellRangeFt(spell.range),
        tokenId,
      });
    },
    cancel: cancelSpellAoeFit,
  };
}

/** The icon itself (a sibling of the cast button, never nested in it). */
export function SpellAoeAimButton({ spell, aim }: { spell: HudSpell; aim: SpellAoeAim }) {
  const { t } = useI18n();
  if (!hasFittableAoe(spell)) return null;
  const disabled = aim.tokenId === null;
  const on = aim.fittingSpell === spell.name;
  const label = disabled ? aim.disabledReason ?? t('spellAoe.aim') : t('spellAoe.aim');
  return (
    <Button
      type="button"
      variant={on ? 'default' : 'outline'}
      size="icon"
      data-testid={`spell-aim-${spell.spellSlug ?? spell.id}`}
      aria-label={label}
      title={label}
      aria-disabled={disabled || undefined}
      aria-pressed={on}
      className={`shrink-0 ${disabled ? 'cursor-not-allowed opacity-45' : ''}`}
      onClick={(e) => { e.stopPropagation(); aim.start(spell); }}
    >
      <Crosshair size={14} aria-hidden />
    </Button>
  );
}

/**
 * After a cast of a spell with an area: offer to fit it — a toast with «Примерить», gone by itself or by one press of
 * «Отмена» (never in the way, nothing is started for the player).
 */
export function offerSpellAoe(spell: HudSpell, aim: SpellAoeAim, t: (key: 'spellAoe.offer' | 'spellAoe.offerAction' | 'spellAoe.cancel') => string): void {
  if (!hasFittableAoe(spell) || aim.tokenId === null) return;
  toast(t('spellAoe.offer').replace('{spell}', spell.name), {
    duration: 8000,
    action: { label: t('spellAoe.offerAction'), onClick: () => aim.start(spell) },
    cancel: { label: t('spellAoe.cancel'), onClick: () => undefined },
  });
}
